import React from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import classNames from 'classnames';

import { Consulting } from '@layouts/Consulting';
import {
	Awards,
	DescriptionWithImage,
	InternalLinking,
	Reviews,
	Technologies,
} from '@components';
import { Disclaimer, Header, Quote } from './components';
import {
	internalLinkingData,
	mobileApplicationsData,
	webApplicationsData,
} from '@store/CustomSoftwareData';
import SoftwareSolutionImage from '@static/images/figures/software-solutions.webp';
import SupportImage from '@static/images/figures/support.webp';
import QChangeCaseStudyImage from '@static/images/figures/qchange-case-study.webp';
import IndaHashCaseStudyImage from '@static/images/figures/indahash-case-study.webp';
import { PageUrls } from '@utils/constants/PageUrls';

import * as styles from './CustomSoftware.module.scss';
import useReviewsQuery from '@utils/hooks/useReviewsQuery';

export const CustomSoftware: React.FC = () => {
	const { directus } = useStaticQuery(graphql`
		query CustomSoftwareReviews {
			directus {
				PageReviews(filter: { pageName: { _contains: "software" } }) {
					Reviews {
						Reviews_id {
							quote
							reviewerName
							reviewerPosition
							link
							title
							logo {
								id
								imageFile {
									extension
									publicURL
									childImageSharp {
										gatsbyImageData
									}
								}
							}
						}
					}
				}
			}
		}
	`);
	const reviews = useReviewsQuery(directus);

	return (
		<div className="z-99 relative overflow-hidden">
			<Header />
			<DescriptionWithImage
				className="gray-bg"
				imageClassName={styles.image}
				image={SoftwareSolutionImage}
				imageOnRight
				disableContentPadding
			>
				<h2 className="font-size-md mb-2">All in one</h2>
				<p className="mb-4 font-size-sm">
					The spectrum of our competencies includes developing software for IT
					systems, server software, applications for mobile devices, software
					for dedicated systems and workstations, as well as libraries, modules,
					UI and software development components.
				</p>
				<h2 className="font-size-md mb-2 mt-5">
					We are building software solutions
				</h2>
				<p className="mb-4 font-size-sm">
					Our specialists work with programming languages such as Python,
					JavaScript, C and more. We are experienced with embedded software that
					is used to control machines and devices not typically considered
					computers — telecommunications networks, cars, industrial robots and
					more. Devices can be connected as part of the Internet of Things
					(IoT). We build application software that will be the best solution
					for your smartphone.
				</p>
			</DescriptionWithImage>
			<Disclaimer />
			<div className={styles.scroll} id="technologies">
				<Technologies
					title="Web Applications"
					technologiesData={webApplicationsData}
				/>
			</div>
			<DescriptionWithImage
				imageClassName={styles.image}
				image={QChangeCaseStudyImage}
				imageOnRight
			>
				<p className="font-size-small-title mb-3 font-600">
					Platform for management
				</p>
				<p className="font-size-sm mb-4">
					qChange platform lets you measure your leadership experience gap, give
					and receive real-time feedback in an easy and safe way.
				</p>
				<Link
					to={PageUrls.QCHANGE_CASE_STUDY}
					className={classNames(styles.link, 'bttn bttn-orange-border')}
				>
					See full case study
				</Link>
			</DescriptionWithImage>
			<Technologies
				title="Mobile Applications"
				technologiesData={mobileApplicationsData}
			/>
			<DescriptionWithImage
				className={classNames(styles.typography, 'white-bg')}
				imageClassName={styles.indahashImage}
				image={IndaHashCaseStudyImage}
			>
				<p className="font-size-small-title mb-3 font-600">
					Mobile app for influencers
				</p>
				<p className="font-size-sm mb-4">
					Mobile integration through Social Media is a core feature of indaHash.
					This is the simplest way to connect influencers with brands,
					everything accessible from a mobile app!
				</p>
				<Link
					to={PageUrls.INDAHASH_CASE_STUDY}
					className={classNames(styles.link, 'bttn bttn-orange-border')}
				>
					See full case study
				</Link>
			</DescriptionWithImage>
			<DescriptionWithImage
				className="gray-bg"
				imageClassName={styles.image}
				imageOnRight
				image={SupportImage}
				disableContentPadding
			>
				<p className="font-size-small-title mb-2 font-600">
					Creating, designing, deploying and supporting.
				</p>
				<p className="mb-4 font-size-sm">
					The whole development process is based on technical and
					functional/visual documentation and an effective methodology of
					product management. The source code is stored in decentralised version
					control systems and continuously monitored for compliance with
					standards and regularities of applied procedures.
				</p>

				<p className="mb-4 font-size-sm">
					The final product is transferred to the client together with
					copyrights, codes and detailed documentation. EMBIQ also ensures the
					building instruction and the description of implementation procedures.
				</p>
			</DescriptionWithImage>
			<InternalLinking className="white-bg" links={internalLinkingData} />
			<Quote />
			{!!reviews.length && (
				<Reviews
					title="Our client says"
					reviews={reviews}
					maxWidth="1300px"
					bg="white"
					titleClass="font-size-medium-title"
				/>
			)}
			<Awards className="gray-bg" />
			<Consulting />
		</div>
	);
};
